import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

export interface FormFieldProps {
  label: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  required?: boolean;
  optional?: boolean;
  /** Surface tone. `default` = light app canvas; `on-dark` = immersive glass. */
  tone?: 'default' | 'on-dark';
  children: ReactElement<{ id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean; required?: boolean }>;
  className?: string;
}

export function FormField({
  label,
  helperText,
  errorText,
  required,
  optional,
  tone = 'default',
  children,
  className,
}: FormFieldProps) {
  const reactId = useId();
  const inputId = children.props.id ?? `field-${reactId}`;
  const helperId = helperText ? `${inputId}-helper` : undefined;
  const errorId = errorText ? `${inputId}-error` : undefined;
  const describedBy = [children.props['aria-describedby'], helperId, errorId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(errorText);
  const onDark = tone === 'on-dark';

  const child = isValidElement(children)
    ? cloneElement(children, {
        id: inputId,
        'aria-describedby': describedBy,
        'aria-invalid': invalid || children.props['aria-invalid'] || undefined,
        required,
      })
    : children;

  // On dark glass the label/helper read from the on-glass text ramp and errors
  // use the lighter danger ramp (#FCA5A5) so they clear AA over the charcoal.
  const labelClass = onDark ? 'text-body-sm font-medium text-white/90' : 'text-body-sm font-medium text-text-strong';
  const requiredClass = onDark ? 'ml-0.5 text-[#FCA5A5]' : 'ml-0.5 text-danger';
  const optionalClass = onDark ? 'ml-1 text-white/60 text-caption font-normal' : 'ml-1 text-text-subtle text-caption font-normal';
  const helperClass = onDark ? 'text-body-sm text-white/70' : 'text-body-sm text-text-muted';
  const errorClass = onDark ? 'text-body-sm text-[#FCA5A5]' : 'text-body-sm text-danger';

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={inputId} className={labelClass}>
        {label}
        {required ? <span className={requiredClass} aria-hidden> *</span> : null}
        {optional && !required ? <span className={optionalClass}>(optional)</span> : null}
      </label>
      {child}
      {helperText && !errorText ? (
        <span id={helperId} className={helperClass}>
          {helperText}
        </span>
      ) : null}
      {errorText ? (
        <span id={errorId} role="alert" className={errorClass}>
          {errorText}
        </span>
      ) : null}
    </div>
  );
}
